import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import {
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
} from "@/common/styles";
import {
  debounce,
  getDayjsObject,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
  hasCheckSecurity,
} from "@/util/common_function";
import { MODULES_LIST, SCHEMA_NAME } from "@/util/common_variable";
import { Form, TimePicker } from "antd";
import dayjs from "dayjs";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useSelector } from "react-redux";
import { Arbitrary_Names, Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { FieldSecurityProvider } from "@/contexts/FieldSecurityContext";
import SecureFormItem from "@/common/SecureFormItem";
import ApprovalReadOnlyValue from "./ApprovalReadOnlyValue";

const Supervisor = forwardRef(({
  data,
  updateForm = null,
  parentFieldEnable,
  fromkey = "",
  startingPoint="",
}, ref) => {
  const { userDetail } = useSelector((s) => s?.userDetail);
  const { bookingsFieldsEnabled } = useSelector((state) => state?.bookings);
  const [form] = Form.useForm();
  const [supervisorData, setSupervisorData] = useState(null);

  const status = Form.useWatch(
    isNonEmptyVariable(fromkey)
      ? [fromkey, 0, "supervisorStatus"]
      : "supervisorStatus",
    form,
  );

  const allOptions = getOptionsFromRefList("JailIncidentsStatusAdministrator");

  // Status starts blank on a fresh record and stays whatever the user
  // actually picks - it's no longer defaulted to Pending here. Save
  // Changes (JailIncidentDetail.jsx's saveJailIncident -> withDefaultStatus)
  // is what fills in "Pending" for any status still unset at save time,
  // without touching one the user already set to something else.
  useEffect(() => {
    if (isNonEmptyVariable(data)) {
      setSupervisorData({
        ...data,
        supervisorStaff: isNonEmptyVariable(data?.supervisorStaffId)
          ? getRefListNameByID("", data?.supervisorStaffId, true, true)
          : "",
        supervisorDateTime: getDayjsObject(
          data?.supervisorDate,
          data?.supervisorTime,
        ),
      });
    } else {
      setSupervisorData(null);
    }
  }, [data]);

  useEffect(() => {
    if (isNonEmptyVariable(supervisorData)) {
      if (isNonEmptyVariable(fromkey)) {
        form.setFieldsValue({
          [fromkey]: [supervisorData],
        });
      } else {
        form.setFieldsValue(supervisorData);
      }
    }
  }, [supervisorData]);

  const changeStatus = (e) => {
    if (!isNonEmptyVariable(e)) return;
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "supervisorDateTime"]
        : "supervisorDateTime",
      dayjs(),
    );
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "supervisorStaffId"]
        : "supervisorStaffId",
      userDetail?.staffId,
    );
    form.setFieldValue(
      isNonEmptyVariable(fromkey)
        ? [fromkey, 0, "supervisorRejectReason"]
        : "supervisorRejectReason",
      "",
    );

    // Push the status change to the parent immediately instead of relying
    // solely on the 800ms debounced onFieldsChange -> dataModify round-trip
    // below. Save Changes reads jailIncident straight out of redux to decide
    // Open/Closed - if the user changes Status and clicks Save before the
    // debounce fires, the parent still sees the old status and both the
    // saved approval fields and the computed overallStatus are wrong until
    // a second save (same class of bug already worked around for the
    // default "Pending" push above).
    if (isNonEmptyVariable(updateForm)) {
      updateForm({ ...buildSupervisorData(), supervisorStatus: e });
    }
  };

  // Shapes whatever is currently in the (possibly nested, via fromkey) form
  // fields into the supervisorDate/supervisorTime payload shape callers
  // expect. Reads live field state - no validation gate - so it always
  // reflects "the selected one inside the form" at the instant it's called.
  const buildSupervisorData = () => {
    const formValues = form.getFieldsValue(true);
    let values = formValues;
    if (isNonEmptyVariable(fromkey)) {
      values = formValues?.[fromkey]?.[0];
    }

    const _supervisorDateTime = values?.supervisorDateTime
      ? dayjs(values?.supervisorDateTime)?.format("MM/DD/YYYY HH:mm")
      : null;

    // Only the fields Supervisor actually owns. `values` can also carry
    // unrelated fields (e.g. administratorStatus) dragged in by the [data]
    // effect above spreading the WHOLE inmate row into this form's store -
    // returning those here would let a stale copy clobber Administrator's
    // own (correct) value when the two are merged in getApprovalValues().
    return {
      supervisorStaffId: values?.supervisorStaffId,
      supervisorStatus: values?.supervisorStatus,
      supervisorRejectReason: values?.supervisorRejectReason,
      supervisorDate: _supervisorDateTime?.split(" ")?.[0],
      supervisorTime: _supervisorDateTime?.split(" ")?.[1],
    };
  };

  // Lets a parent (e.g. the Involved Inmate modal's Save) pull the current
  // Supervisor selection synchronously at submit time, instead of relying
  // solely on the debounced onFieldsChange -> updateForm round-trip below,
  // which can lose the selection if Save is clicked before it fires.
  useImperativeHandle(ref, () => ({
    getValues: buildSupervisorData,
  }));

  const dataModify = debounce(() => {
    updateForm(buildSupervisorData());
  }, 800);

  const hasSupPermission = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.arbitary,
    LABELS_CAPITAL.VIEW,
    Arbitrary_Names.SupView
  );

  // A user with Admin-View permission outranks a Supervisor approval and
  // can keep editing even once this section is Approved - Admin approval
  // is the only thing that locks them out too (see Administrator.jsx,
  // which has no such override).
  const hasAdminPermission = hasCheckSecurity(
    Security_Types.functionality,
    Entity_types.arbitary,
    LABELS_CAPITAL.VIEW,
    Arbitrary_Names.AdminView
  );

  // Once this section is Approved it's locked for editing, same rule at
  // every level (main/inmate/supplement) since they all render this same
  // component - Unlock (resets status back to Pending) is the only way
  // back in for anyone without Admin-View, rather than parentFieldEnable/
  // Modify mode alone.
  // Matches JailIncidentsInvolvedInmateInterface.asp/HandleEnableControlModule
  // exactly: this is an OR of two independent triggers, not just this
  // section's own status. Administrator = Approved locks Supervisor too,
  // and does so unconditionally - no Admin-View bypass for that clause,
  // unlike the Supervisor-Approved clause below. `data` already carries
  // the sibling administratorStatus alongside supervisorStatus (same row/
  // record), so no extra prop is needed to read it.
  const isApproved = status?.trim?.() === "Approved";
  const isAdministratorApproved =
    data?.administratorStatus?.trim?.() === "Approved";
  const lockedByOwnApproval =
    isAdministratorApproved || (isApproved && !hasAdminPermission);

  return (
    <>
      <StyledSectionHeading marginBottom="15px">
        <span>Supervisor</span>
      </StyledSectionHeading>
      <FieldSecurityProvider
        entity={SCHEMA_NAME.JAIL_INCIDENTS_APPROVAL}
        startingPoint={startingPoint}
        bookingsFieldsEnabled={bookingsFieldsEnabled && parentFieldEnable && hasSupPermission}
      >
        <Form
          form={form}
          onFieldsChange={() => {
            if (isNonEmptyVariable(updateForm)) dataModify();
          }}
        >
          <StyledGridRow gutter={[16]}>
            <StyledGridCol lg={12}>
              <SecureFormItem
                fieldName={ isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorStaffId"]
                    : "supervisorStaffId"}
                formItemProps={{
                  label: "Supervisor Name",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorStaffId"]
                    : "supervisorStaffId",
                }}
                overrideDisabled={lockedByOwnApproval}
              >
                {hasSupPermission ? (
                  <Select
                    id="supervisorStaffId"
                    options={getOptionsFromRefList("", true, true, true)}
                    placeholder="Select Supervisor"
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                  />
                ) : (
                  <ApprovalReadOnlyValue
                    options={getOptionsFromRefList("", true, true, true)}
                  />
                )}
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName={ isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorStatus"]
                    : "supervisorStatus"}
                formItemProps={{
                  label: "Status",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorStatus"]
                    : "supervisorStatus",
                }}
                overrideDisabled={lockedByOwnApproval}
              >
                {hasSupPermission ? (
                  <Select
                    id="supervisorStatus"
                    options={allOptions}
                    placeholder="Select Status"
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                    onChange={changeStatus}
                  />
                ) : (
                  <ApprovalReadOnlyValue options={allOptions} />
                )}
              </SecureFormItem>
            </StyledGridCol>
            <StyledGridCol lg={6}>
              <SecureFormItem
                fieldName={ isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorDateTime"]
                    : "supervisorDateTime"}
                formItemProps={{
                  label: "Date & Time",
                  name: isNonEmptyVariable(fromkey)
                    ? [fromkey, 0, "supervisorDateTime"]
                    : "supervisorDateTime",
                }}
                overrideDisabled={lockedByOwnApproval}
              >
                <CommonDatePicker
                  id="reportingDateTime"
                  format="MM/DD/YYYY HH:mm"
                  disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                />
              </SecureFormItem>
            </StyledGridCol>
            {status === "Rejected" && (
              <StyledGridCol xs={24} lg={24}>
                <SecureFormItem
                  fieldName={ isNonEmptyVariable(fromkey)
                        ? [fromkey, 0, "supervisorRejectReason"]
                        : "supervisorRejectReason"}
                  formItemProps={{ 
                    label: "Reject Reason",
                    name: isNonEmptyVariable(fromkey)
                        ? [fromkey, 0, "supervisorRejectReason"]
                        : "supervisorRejectReason",
                  }}
                >
                  <TextField
                    inputType="textarea"
                    rows="2"
                    disabled={!bookingsFieldsEnabled || !parentFieldEnable}
                  />
                </SecureFormItem>
              </StyledGridCol>
            )}
          </StyledGridRow>
        </Form>
      </FieldSecurityProvider>
    </>
  );
});

Supervisor.displayName = "Supervisor";

export default Supervisor;
